<template>
    <EDCHeader :logo_upc="true" />
    <div class="news-body">
        <div class="news-body__left">
            <div class="news-body__left-header">
                <h3 class="news-body__left-title">Micrositios Municipales</h3>
                <p class="news-body__left-description">
                    Descubre información actualizada de cada municipio: agenda cultural, sitios turísticos, eventos y servicios.
                </p>
            </div>
            <div class="news-municipio__list">
                <p
                    @click="onFilterMunicipio(municipio.code)"
                    class="news-municipio" 
                    :class="[filtered == municipio.code ? 'active' : '']" 
                    v-for="municipio in municipio_list"
                    :key="municipio.code">
                    {{ municipio.name }}
                </p>
            </div>
        </div>
        <div class="news-body__content">
            <!-- Solo mostrar el título cuando NO esté en "todos" -->
            <div v-if="filtered !== 'all'" class="edc-section-head">
                <h1 class="edc-section-head__title">Noticias</h1>
                <p class="edc-section-head__description">
                    En los diferentes municipios del Cesar puedes encontrar noticias relevantes.
                </p>
            </div>
            <div class="mobile-micrositios">
                <h3 class="mobile-micrositios__title">Micrositios Municipales</h3>
                <p class="mobile-micrositios__description">
                    Descubre información actualizada de cada municipio: agenda cultural, sitios turísticos, eventos y servicios.
                </p>
            </div>
            <div class="edc-selector">
                <select name="municipio_selector" id="municipio_selector" @change="onFilterMunicipio()">
                    <option :value="municipio.code" :key="municipio.code" v-for="municipio in municipio_list">{{ municipio.name }}</option>
                </select>
            </div>
            <!-- Tabs para Eventos y Comunicados cuando está en "todos" -->
            <div v-if="filtered === 'all'" class="tabs-section">
                <div class="tabs-navigation">
                    <button 
                        class="tab-button" 
                        :class="{ 'tab-button--active': activeTab === 'eventos' }"
                        @click="activeTab = 'eventos'"
                    >
                        Eventos destacados
                    </button>
                    <button 
                        class="tab-button" 
                        :class="{ 'tab-button--active': activeTab === 'comunicados' }"
                        @click="activeTab = 'comunicados'"
                    >
                        Comunicados
                    </button>
                </div>

                <!-- Contenido de Eventos destacados -->
                <div v-show="activeTab === 'eventos'" class="tab-content">
                    <div class="featured-images-grid">
                        <h2 class="grid-title">Eventos destacados del Cesar</h2>
                        <p class="grid-description">Espacio dedicado a promover y difundir los eventos más importantes del departamento. Aquí encontrarás información sobre actividades culturales, foros, festivales y encuentros que enriquecen la vida del Cesar y fortalecen su identidad turística y cultural.</p>
                        <div class="images-grid">
                            <div 
                                v-for="item in featuredImages" 
                                :key="`featured-${item.id}`"
                                class="image-card"
                            >
                                <div class="image-card__image-wrapper">
                                    <img 
                                        :src="item.image" 
                                        :alt="item.title" 
                                        class="grid-image"
                                        @load="onImageLoad($event, item)"
                                    />
                                </div>
                                <div class="image-content">
                                    <h3 class="image-title">{{ item.title }}</h3>
                                    <p class="image-description">{{ item.description }}</p>
                                    <a v-if="item.link" :href="item.link" target="_blank" class="image-link">
                                        {{ item.linkText || 'Más información' }}
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Contenido de Comunicados -->
                <div v-show="activeTab === 'comunicados'" class="tab-content">
                    <div class="comunicados-section">
                        <h2 class="comunicados-title">Comunicados</h2>
                        <p class="comunicados-description">Espacio informativo donde encontrarás los comunicados oficiales del proyecto Enamórate del Cesar. Mantente al día con las noticias, anuncios y actualizaciones más relevantes sobre el turismo, la cultura y el desarrollo del departamento.</p>
                        <div class="images-grid">
                            <div 
                                v-for="single in news"
                                :key="single.id"
                                class="image-card"
                            >
                                <div class="image-card__image-wrapper">
                                    <img 
                                        :src="single.image" 
                                        :alt="single.title" 
                                        class="grid-image"
                                    />
                                </div>
                                <div class="image-content">
                                    <h3 class="image-title">{{ single.title }}</h3>
                                    <router-link :to="{ name: 'new-details', params: { id: single.id } }" class="image-link">
                                        Ver más
                                    </router-link>
                                </div>
                            </div>
                        </div>
                        <EDCButton label="Cargar más" v-show="next" @click="onNextButton()" />
                    </div>
                </div>
            </div>
            
            <div v-else class="news-list">
                <EDCNew 
                v-for="single in news"
                :key="single.id"
                :id="single.id" 
                :date="formatDate(single.created)" 
                :name="single.title" 
                :image="single.image" />
            </div>
            <EDCButton v-if="filtered !== 'all'" label="Cargar más" v-show="next" @click="onNextButton()" />
        </div>
    </div>
    <EDCFooter />
    <EDCMobileNav />
</template>

<script>
import EDCHeader from '../components/Header.vue';
import EDCFooter from '../components/Footer.vue';
import EDCMobileNav from '../components/MobileNav.vue';
import EDCNew from '../components/New.vue';
import EDCButton from '../components/Button.vue';
import { Spinner } from '@/scripts/open-spinner';

import { MUNICIPIO_CODES_ALL } from '../scripts/municipio-codes';
import { NewsAPI } from '@/services/news';

export default {
    name: "edc-businesses-view",
    components: {
        EDCHeader,
        EDCFooter,
        EDCMobileNav,
        EDCNew,
        EDCButton
    },
    data() {
        return {
            filtered: 'all',
            news: [],
            next: null,
            municipio_list: MUNICIPIO_CODES_ALL,
            activeTab: 'eventos',
            featuredImages: [
                {
                    id: 'featured-1',
                    image: 'https://almacenamientounicesar.blob.core.windows.net/enamoratedelcesar/assets/media/media/business/image_item/CesarDestinoVerdeEvento.jpeg',
                    title: 'Cesar Destino verde',
                    description: 'Un espacio especial para resaltar el turismo de naturaleza como una estrategia esencial de conservación y, al mismo tiempo, como un valioso activo para el desarrollo sostenible del Cesar.',
                    link: 'https://forms.office.com/r/hN985TPwBg',
                    linkText: 'Inscribirse aquí'
                },
                {
                    id: 'featured-2',
                    image: 'https://almacenamientounicesar.blob.core.windows.net/enamoratedelcesar/assets/media/media/business/image_item/PremioVIllazon.jpg',
                    title: 'Premios Julio Villazón Baquero – XV Reconocimiento al Mérito Empresarial',
                    description: 'Será una noche para honrar a quienes, con su trabajo y pasión, inspiran el desarrollo, el progreso y el orgullo de ser parte de esta tierra emprendedora.'
                }
            ]
        }
    },
    mounted() {
        this.getAllNews()
    },
    methods: {
        getAllNews() {
            Spinner.show()
            NewsAPI.allNews().then(data => {
                this.news = data.results;
                this.next = data.next;
                Spinner.hide()
            })
        },
        getFilteredNews(municipio_name) {
            NewsAPI.newsFilteredByMunicipio(municipio_name).then(data => {
                this.news = data.results;
                this.next = data.next;
            })
        },
        onFilterMunicipio(municipio_name) {
            const selector = document.querySelector("#municipio_selector")
            
            let selectedCode;
            if (!municipio_name) {
                selectedCode = selector.value;
            } else {
                selectedCode = municipio_name;
            }

            if (selectedCode === "valledupar") {
                window.location.href = "https://valledupar.enamoratedelcesar.co/";
                return;
            }
            
            if (selectedCode === "aguachica") {
                window.location.href = "https://aguachica.enamoratedelcesar.co/";
                return;
            }

            if (selectedCode === "agustin_codazzi") {
                window.location.href = "https://agustin-codazzi.enamoratedelcesar.co/";
                return;
            } 

            if (selectedCode === "astrea") {
                window.location.href = "https://astrea.enamoratedelcesar.co/";
                return;
            } 

            if (selectedCode === "becerril") {
                window.location.href = "https://becerril.enamoratedelcesar.co/";
                return;
            } 

            if (selectedCode === "bosconia") {
                window.location.href = "https://bosconia.enamoratedelcesar.co/";
                return;
            } 

            if (selectedCode === "chimichagua") {
                window.location.href = "https://chimichagua.enamoratedelcesar.co/";
                return;
            } 

            if (selectedCode === "chiriguana") {
                window.location.href = "https://chiriguana.enamoratedelcesar.co/";
                return;
            } 

            if (selectedCode === "curumani") {
                window.location.href = "https://curumani.enamoratedelcesar.co/";
                return;
            } 

            if (selectedCode === "el_copei") {
                window.location.href = "https://el-copey.enamoratedelcesar.co/";
                return;
            } 
            
            if (selectedCode === "el_paso") {
                window.location.href = "https://el-paso.enamoratedelcesar.co/";
                return;
            } 

            if (selectedCode === "gamarra") {
                window.location.href = "https://gamarra.enamoratedelcesar.co/";
                return;
            } 

            if (selectedCode === "gonzalez") {
                window.location.href = "https://gonzalez.enamoratedelcesar.co/";
                return;
            } 

            if (selectedCode === "la_gloria") {
                window.location.href = "https://la-gloria.enamoratedelcesar.co/";
                return;
            } 

            if (selectedCode === "la_jagua_de_ibirico") {
                window.location.href = "https://la-jagua-de-ibirico.enamoratedelcesar.co/";
                return;
            }            

            if (selectedCode === "la_paz") {
                window.location.href = "https://la-paz.enamoratedelcesar.co/";
                return;
            }                    

            if (selectedCode === "manaure") {
                window.location.href = "https://manaure-balcon-del-cesar.enamoratedelcesar.co/";
                return;
            }  

            if (selectedCode === "pailitas") {
                window.location.href = "https://pailitas.enamoratedelcesar.co/";
                return;
            }  

            if (selectedCode === "pelaya") {
                window.location.href = "https://pelaya.enamoratedelcesar.co/";
                return;
            }  

            if (selectedCode === "pueblo_bello") {
                window.location.href = "https://pueblo-bello.enamoratedelcesar.co/";
                return;
            }  

            if (selectedCode === "rio_de_oro") {
                window.location.href = "https://rio-de-oro.enamoratedelcesar.co/";
                return;
            }  

            if (selectedCode === "san_alberto") {
                window.location.href = "https://san-alberto.enamoratedelcesar.co/";
                return;
            }  

            if (selectedCode === "san_diego") {
                window.location.href = "https://san-diego.enamoratedelcesar.co/";
                return;
            }  

            if (selectedCode === "san_martin") {
                window.location.href = "https://san-martin.enamoratedelcesar.co/";
                return;
            }  

            if (selectedCode === "tamalameque") {
                window.location.href = "https://tamalameque.enamoratedelcesar.co/";
                return;
            }

            // if (!municipio_name) {
            //     this.filtered = selector.value;
            // } else {
            //     this.filtered = municipio_name;
            //     selector.value = this.filtered;
            // }

            // if (this.filtered === "valledupar") {
            //     window.location.href = "https://valledupar.enamoratedelcesar.co/";
            //     return;
            // }
            
            // if (this.filtered === "aguachica") {
            //     window.location.href = "https://aguachica.enamoratedelcesar.co/";
            //     return;
            // }

            // if (this.filtered === "agustin_codazzi") {
            //     window.location.href = "https://agustin-codazzi.enamoratedelcesar.co/";
            //     return;
            // } 

            // if (this.filtered === "astrea") {
            //     window.location.href = "https://astrea.enamoratedelcesar.co/";
            //     return;
            // } 

            // if (this.filtered === "becerril") {
            //     window.location.href = "https://becerril.enamoratedelcesar.co/";
            //     return;
            // } 

            // if (this.filtered === "bosconia") {
            //     window.location.href = "https://bosconia.enamoratedelcesar.co/";
            //     return;
            // } 

            // if (this.filtered === "chimichagua") {
            //     window.location.href = "https://chimichagua.enamoratedelcesar.co/";
            //     return;
            // } 

            // if (this.filtered === "chiriguana") {
            //     window.location.href = "https://chiriguana.enamoratedelcesar.co/";
            //     return;
            // } 

            // if (this.filtered === "curumani") {
            //     window.location.href = "https://curumani.enamoratedelcesar.co/";
            //     return;
            // } 

            // if (this.filtered === "el_copei") {
            //     window.location.href = "https://el-copey.enamoratedelcesar.co/";
            //     return;
            // } 
            
            // if (this.filtered === "el_paso") {
            //     window.location.href = "https://el-paso.enamoratedelcesar.co/";
            //     return;
            // } 

            // if (this.filtered === "gamarra") {
            //     window.location.href = "https://gamarra.enamoratedelcesar.co/";
            //     return;
            // } 

            // if (this.filtered === "gonzalez") {
            //     window.location.href = "https://gonzalez.enamoratedelcesar.co/";
            //     return;
            // } 

            // if (this.filtered === "la_gloria") {
            //     window.location.href = "https://la-gloria.enamoratedelcesar.co/";
            //     return;
            // } 

            // if (this.filtered === "la_jagua_de_ibirico") {
            //     window.location.href = "https://la-jagua-de-ibirico.enamoratedelcesar.co/";
            //     return;
            // }            

            // if (this.filtered === "la_paz") {
            //     window.location.href = "https://la-paz.enamoratedelcesar.co/";
            //     return;
            // }                    

            // if (this.filtered === "manaure") {
            //     window.location.href = "https://manaure-balcon-del-cesar.enamoratedelcesar.co/";
            //     return;
            // }  

            // if (this.filtered === "pailitas") {
            //     window.location.href = "https://pailitas.enamoratedelcesar.co/";
            //     return;
            // }  

            // if (this.filtered === "pelaya") {
            //     window.location.href = "https://pelaya.enamoratedelcesar.co/";
            //     return;
            // }  

            // if (this.filtered === "pueblo_bello") {
            //     window.location.href = "https://pueblo-bello.enamoratedelcesar.co/";
            //     return;
            // }  

            // if (this.filtered === "rio_de_oro") {
            //     window.location.href = "https://rio-de-oro.enamoratedelcesar.co/";
            //     return;
            // }  

            // if (this.filtered === "san_alberto") {
            //     window.location.href = "https://san-alberto.enamoratedelcesar.co/";
            //     return;
            // }  

            // if (this.filtered === "san_diego") {
            //     window.location.href = "https://san-diego.enamoratedelcesar.co/";
            //     return;
            // }  

            // if (this.filtered === "san_martin") {
            //     window.location.href = "https://san-martin.enamoratedelcesar.co/";
            //     return;
            // }  

            // if (this.filtered === "tamalameque") {
            //     window.location.href = "https://tamalameque.enamoratedelcesar.co/";
            //     return;
            // }

            if (!municipio_name) {
                this.filtered = selector.value;
            } else {
                this.filtered = municipio_name;
                selector.value = this.filtered;
            }

            if (this.filtered === "all") {
                this.getAllNews()
            } else {
                this.getFilteredNews(this.filtered)
            } 
        },
        onNextButton() {
            fetch(this.next)
            .then(res => res.json())
            .then(data => {
                const results = data.data.results;

                if (results) {
                    this.next = data.data.next;

                    results.forEach(exp => {
                        this.experiences.push(exp)
                    })
                }
            })
        },
        formatDate(value){
            if (!value) return '';
            const withT = value.replace(' ', 'T');
            const iso = withT.replace(/([+-]\d{2})(\d{2})$/, '$1:$2');
            const d = new Date(iso);
            if (isNaN(d)) return value;
            return new Intl.DateTimeFormat('es-CO', {
                day: '2-digit',
                month: 'long',
                year: 'numeric'
            }).format(d);
        },
        hasMicrositio(code) {
            const micrositios = [
                'valledupar', 'aguachica', 'agustin_codazzi', 'astrea', 'becerril',
                'bosconia', 'chimichagua', 'chiriguana', 'curumani', 'el_copei',
                'el_paso', 'gamarra', 'gonzalez', 'la_gloria', 'la_jagua_de_ibirico',
                'la_paz', 'manaure', 'pailitas', 'pelaya', 'pueblo_bello',
                'rio_de_oro', 'san_alberto', 'san_diego', 'san_martin', 'tamalameque'
            ];
            return micrositios.includes(code);
        },
        // Métodos para detección dinámica de dimensiones
        onImageLoad(event, item) {
            const img = event.target;
            const aspectRatio = img.naturalWidth / img.naturalHeight;
            
            // Detectar tipo de imagen
            item.aspectRatio = aspectRatio;
            item.isPortrait = aspectRatio < 0.8; // Más alto que ancho
            item.isLandscape = aspectRatio > 1.2; // Más ancho que alto
            item.isSquare = aspectRatio >= 0.8 && aspectRatio <= 1.2; // Cuadrada
        },
        getImageClass(item) {
            if (item.isPortrait) return 'portrait';
            if (item.isLandscape) return 'landscape';
            if (item.isSquare) return 'square';
            return '';
        },
    }
}
</script>

<style>
/* BODY */
.news-body {
    width: 100%;
    max-width: 1000px;
    padding: 24px;
    margin: 0px auto;
    margin-bottom: 40px;
    display: flex;
    gap: 24px;
    overflow-x: hidden;
    box-sizing: border-box;
}

.news-body__left {
    min-width: 160px;
    max-width: 180px;
    display: flex;
    flex-direction: column;
    gap: 16px;
    /* Hide on mobile */
    display: none;
}

.news-body__left-header {
    margin-bottom: 12px;
}

.news-body__left-title {
    font-size: 1.3rem;
    margin: 0 0 8px 0;
    line-height: 1.3;
}

.news-body__left-description {
    font-size: 0.9rem;
    color: rgba(80, 59, 47, 0.7);
    margin: 0;
    line-height: 1.4;
    text-align: justify;
}

.news-municipio__list {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.news-municipio {
    text-decoration: none;
    color: var(--color-dark);
    cursor: pointer;
}

.news-municipio.active {
    color: var(--color-primary);
    font-weight: bold;
}

.news-body__content {
    width: 100%;
    max-width: 100%;
    display: flex;
    flex-direction: column;
    gap: 24px;
    overflow-x: hidden;
    box-sizing: border-box;
}

/* Estilos para micrositios en móvil */
.mobile-micrositios {
    display: block;
    margin-bottom: 20px;
}

.mobile-micrositios__title {
    font-size: 1.4rem;
    font-weight: 700;
    color: var(--color-dark);
    margin: 16px 0 12px 0;
    line-height: 1.3;
    text-align: center;
}

.mobile-micrositios__description {
    font-size: 0.95rem;
    color: rgba(80, 59, 47, 0.75);
    margin: 0 0 3px 0;
    line-height: 1.5;
    text-align: center;
}

.edc-selector {
    margin-bottom: 24px;
    margin-top: 0;
    position: relative;
    width: 100%;
    max-width: 100%;
    box-sizing: border-box;
}

.edc-selector select {
    width: 100%;
    max-width: 100%;
    height: 50px;
    padding: 0px 40px 0px 12px;
    padding-top: 2px;
    font-size: 1.1rem;
    border: 2px solid rgba(161, 83, 170, 0.2);
    border-radius: 8px;
    background: white;
    color: var(--color-dark);
    cursor: pointer;
    transition: all 0.3s ease;
    appearance: none;
    -webkit-appearance: none;
    -moz-appearance: none;
    box-sizing: border-box;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    min-width: 0;
    font-weight: 500;
}

.edc-selector select option {
    max-width: 100%;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    padding: 12px 16px;
    font-size: 1rem;
    background: white;
    color: var(--color-dark);
    cursor: pointer;
}

.edc-selector select option:checked {
    background: rgba(161, 83, 170, 0.1);
    color: var(--color-primary);
    font-weight: 600;
}

.edc-selector select option:hover {
    background: rgba(161, 83, 170, 0.05);
}

.edc-selector::after {
    content: '▼';
    position: absolute;
    width: 30px;
    height: 80%;
    right: 8px;
    top: 50%;
    transform: translateY(-50%);
    background-color: white;
    pointer-events: none;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 0.8rem;
    color: var(--color-dark);
}

.edc-selector select:hover {
    border-color: var(--color-primary);
}

.edc-selector select:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 3px rgba(161, 83, 170, 0.1);
}

.news-body button {
    max-width: 250px;
    margin: 0 auto;
}

.news-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(235px, 1fr));
    gap: 16px;
}

/* Estilos de tabs */
.tabs-section {
    width: 100%;
    margin-bottom: 48px;
}

.tabs-navigation {
    display: flex;
    justify-content: center;
    gap: 8px;
    margin-bottom: 32px;
    border-bottom: 2px solid rgba(161, 83, 170, 0.1);
    padding-bottom: 0;
}

.tab-button {
    background: transparent;
    border: none;
    padding: 12px 24px;
    font-size: 1rem;
    font-weight: 600;
    color: rgba(80, 59, 47, 0.6);
    cursor: pointer;
    transition: all 0.3s ease;
    border-bottom: 3px solid transparent;
    margin-bottom: -2px;
    font-family: 'Overpass', sans-serif;
}

.tab-button:hover {
    color: var(--color-primary);
}

.tab-button--active {
    color: var(--color-primary);
    border-bottom-color: var(--color-primary);
}

.tab-content {
    width: 100%;
}

/* Estilos del grid de imágenes destacadas */
.featured-images-grid {
    width: 100%;
    margin-bottom: 0;
}

/* Estilos de la sección de comunicados */
.comunicados-section {
    width: 100%;
    margin-top: 0;
}

.comunicados-title {
    font-size: 2rem;
    font-weight: 700;
    color: var(--color-dark);
    margin-bottom: 32px;
    text-align: center;
    position: relative;
    padding-bottom: 16px;
}

.comunicados-title::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: var(--color-primary);
    border-radius: 2px;
}

.comunicados-description {
    font-size: 1rem;
    color: rgba(80, 59, 47, 0.75);
    text-align: center;
    max-width: 800px;
    margin: 0 auto 32px auto;
    line-height: 1.6;
}

.grid-title {
    font-size: 2rem;
    font-weight: 700;
    color: var(--color-dark);
    margin-bottom: 32px;
    text-align: center;
    position: relative;
    padding-bottom: 16px;
}

.grid-title::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 60px;
    height: 3px;
    background: var(--color-primary);
    border-radius: 2px;
}

.grid-description {
    font-size: 1rem;
    color: rgba(80, 59, 47, 0.75);
    text-align: center;
    max-width: 800px;
    margin: 0 auto 32px auto;
    line-height: 1.6;
}

.images-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
    gap: 24px;
    margin-bottom: 32px;
}

.image-card {
    border-radius: 20px;
    overflow: hidden;
    background: white;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06), 0 0 0 1px rgba(161, 83, 170, 0.1);
    border: none;
    transition: all 0.3s ease;
    display: flex;
    flex-direction: column;
    height: 100%;
    position: relative;
}

.image-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(161, 83, 170, 0.15), 0 0 0 1px rgba(161, 83, 170, 0.2);
    background: white;
}

.image-card__image-wrapper {
    width: 100%;
    overflow: hidden;
    background: white;
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
}

.grid-image {
    width: 100%;
    height: auto;
    max-height: 320px;
    object-fit: contain;
    display: block;
    transition: transform 0.3s ease;
}

.image-card:hover .grid-image {
    transform: scale(1.03);
}

.image-content {
    padding: 24px;
    display: flex;
    flex-direction: column;
    flex: 1;
    gap: 14px;
    background: transparent;
}

.image-title {
    font-size: 1.3rem;
    font-weight: 700;
    color: var(--color-dark);
    margin: 0;
    line-height: 1.35;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    transition: color 0.3s ease;
    text-align: center;
}

.image-card:hover .image-title {
    color: var(--color-primary);
}

.image-description {
    font-size: 0.95rem;
    color: rgba(80, 59, 47, 0.75);
    margin: 0;
    line-height: 1.6;
    flex: 1;
    text-align: justify;
}

.image-link {
    display: inline-block;
    background: var(--color-primary);
    color: white;
    text-decoration: none;
    font-size: 0.9rem;
    font-weight: 600;
    padding: 10px 20px;
    border-radius: 8px;
    transition: all 0.3s ease;
    margin-top: 8px;
    text-align: center;
}

.image-link:hover {
    background: rgba(161, 83, 170, 0.9);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(161, 83, 170, 0.3);
}

.image-link:hover {
    background: rgba(161, 83, 170, 0.9);
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(161, 83, 170, 0.3);
}


@media (min-width: 800px) {
    .news-body__left { display: flex }
    .news-body__content .edc-selector { display: none }
    .mobile-micrositios { display: none }
    .images-grid {
        grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
        gap: 28px;
    }
    .grid-image {
        max-height: 380px;
    }
    .grid-title {
        font-size: 2.2rem;
    }
}

@media (max-width: 480px) {
    .images-grid {
        grid-template-columns: 1fr;
        gap: 20px;
    }
    .grid-image {
        max-height: 280px;
    }
    .image-content {
        padding: 16px;
    }
    .grid-title {
        font-size: 1.6rem;
        margin-bottom: 24px;
        padding-bottom: 12px;
    }
    .grid-title::after {
        width: 50px;
        height: 2px;
    }
    .edc-selector {
        width: 100%;
        max-width: 100%;
        box-sizing: border-box;
    }
    .edc-selector select {
        width: 100%;
        max-width: 100%;
        font-size: 1rem;
        padding-right: 35px;
        min-width: 0;
        height: 48px;
    }
    .edc-selector select option {
        font-size: 0.95rem;
        padding: 10px 14px;
        max-width: calc(100vw - 32px);
    }
    .news-body {
        padding: 16px;
        overflow-x: hidden;
    }
    .news-body__content {
        width: 100%;
        max-width: 100%;
        overflow-x: hidden;
    }
}
</style>